import { IOption } from "component-library";

import { anyHeightRange, anyPlayingAgeRange } from "shared/config/common";
import { formatImperialLength } from "shared/helpers/formatters";
import {
  convertImperialToMetric,
  convertMetersToCentimeters,
  convertMetricToImperial,
} from "shared/helpers/unitConverters";
import { IPaginatedAgentLists } from "shared/models/agentList";
import {
  BreakdownId,
  DisabilityPreference,
  IBreakdown,
  IRole,
  LinkmailType,
} from "shared/models/breakdown";
import { IFacetGroup } from "shared/models/facet";
import { ICountry } from "shared/models/location";
import { attachmentsApiUrl } from "shared/services/api/attachments/attachmentService";
import dateService, { dateFormats } from "shared/services/dateService";

import { findFacetName, findFacetNames } from "./facetHelpers";

export interface IPrepareSentTexts {
  sentByFormat: string;
  sentByAnonymous: string;
  dateTimeFormat: string;
  sentOnFormat: string;
  sentDatePlaceholder?: string;
}

export interface IPrepareRoleTexts {
  roleAnyPlayingAge: string;
  roleAnyHeight: string;
  roleAnyGender: string;
  roleAnyAppearance: string;
  rolePlayingAgeFormat: string;
}

export interface IPrepareBreakdownViewData {
  productionTypes: IFacetGroup[];
  contractTypes: IFacetGroup[];
  genders: IFacetGroup[];
  appearances: IFacetGroup[];
  disabilities: IFacetGroup[];
  countries: ICountry[];
  agentLists: IPaginatedAgentLists;
}

export interface IPrepareBreakdownTitleData {
  title: string;
  productionTypeId: number;
  linkmailType: LinkmailType;
  amendmentCount: number;
}

export interface IPrepareBreakdownTitleOptions {
  productionTypes?: IFacetGroup[];
  linkmailTypeAmendment: string;
}

export interface IPrepareClosingDateTexts {
  today: string;
  tomorrow: string;
  other: string;
  dateTimeFormat: string;
}

export interface IPrepareClosingDate2Texts {
  closed: string;
  today: string;
  tomorrow: string;
  other: string;
  dateTimeFormat: string;
}

interface IPrepareViewSuggestionsLinkData {
  id: BreakdownId;
  linkmailType: LinkmailType;
  amendmentParentId: string | null;
}

export const formatDateTime = (dateTimeFormat: string, date: Date | string) =>
  dateTimeFormat
    .replace("{date}", dateService.format(date, dateFormats.date))
    .replace("{time}", dateService.format(date, dateFormats.timeShort));

export function prepareBreakdownDataTitle(
  breakdownData: IPrepareBreakdownTitleData,
  options: IPrepareBreakdownTitleOptions
) {
  const productionTypePart = options.productionTypes
    ? findFacetName(options.productionTypes, breakdownData.productionTypeId)
    : undefined;

  const linkmailTypePart =
    breakdownData.linkmailType === LinkmailType.Amendment
      ? options.linkmailTypeAmendment.replace(
          "{number}",
          breakdownData.amendmentCount.toString()
        )
      : undefined;

  const titleParts = [
    breakdownData.title,
    productionTypePart && `(${productionTypePart})`,
    linkmailTypePart && `(${linkmailTypePart})`,
  ];

  return titleParts.filter(Boolean).join(" ");
}

export function prepareBreakdownTitle(
  breakdown: IBreakdown,
  options: IPrepareBreakdownTitleOptions
) {
  return prepareBreakdownDataTitle(
    {
      title: breakdown.production.title,
      productionTypeId: breakdown.production.typeId,
      linkmailType: breakdown.linkmailType,
      amendmentCount: breakdown.amendmentCount,
    },
    options
  );
}

export function prepareSent(texts: IPrepareSentTexts, breakdown: IBreakdown) {
  const sentBy = breakdown.sentBy
    ? texts.sentByFormat.replace("{sender}", breakdown.sentBy)
    : texts.sentByAnonymous;

  const dateString = breakdown.sent;
  const sentOnDate = !dateString
    ? texts.sentDatePlaceholder
    : formatDateTime(texts.dateTimeFormat, dateString);
  const sentOn = texts.sentOnFormat.replace("{date}", sentOnDate!);

  return `${sentBy} ${sentOn}`.trim();
}

/** @obsolete Use prepareClosingDate2 instead */
export function prepareClosingDate(
  texts: IPrepareClosingDateTexts,
  dateString: string
) {
  if (dateService.isToday(dateString)) {
    return texts.today;
  }

  if (dateService.isTomorrow(dateString)) {
    return texts.tomorrow;
  }

  const formattedDate = formatDateTime(texts.dateTimeFormat, dateString);

  return texts.other.replace("{date}", formattedDate);
}

export function prepareClosingDate2(
  texts: IPrepareClosingDate2Texts,
  closingDate: Date | string,
  freezeDate: Date | string | null | undefined
) {
  const getClosingDateFormat = () => {
    if (freezeDate != null || dateService.isBeforeNow(closingDate)) {
      return texts.closed;
    }

    if (dateService.isToday(closingDate)) {
      return texts.today;
    }

    if (dateService.isTomorrow(closingDate)) {
      return texts.tomorrow;
    }

    return texts.other;
  };

  let closingDateFormat = getClosingDateFormat();

  if (!closingDateFormat.includes("{time}")) {
    closingDateFormat = closingDateFormat.replace(
      "{date}",
      texts.dateTimeFormat
    );
  }

  return formatDateTime(closingDateFormat, closingDate);
}

export function prepareHeightRange(
  minimumHeight: number | null,
  maximumHeight: number | null,
  textsRoleAnyHeight: string
) {
  const min = minimumHeight ?? anyHeightRange.min;
  const max = maximumHeight ?? anyHeightRange.max;

  if (min <= anyHeightRange.min && max >= anyHeightRange.max) {
    return textsRoleAnyHeight;
  }

  const minHeight = convertMetersToCentimeters(min).toFixed(0);
  const maxHeight = convertMetersToCentimeters(max).toFixed(0);

  return `${minHeight} — ${maxHeight} cm`;
}

export function prepareLocation(
  location: string,
  countryCode: string | null = "",
  countries: ICountry[]
) {
  const countryName = countryCode
    ? countries.find(
        (country) =>
          country.countryCode.toLowerCase() === countryCode.toLowerCase()
      )?.localisedCountryName
    : undefined;

  return `${location ?? ""}${location && countryName ? ", " : ""}${
    countryName ?? ""
  }`;
}

export function preparePlayingAge(
  minimumPlayingAge: number | null,
  maximumPlayingAge: number | null,
  roleAnyPlayingAgeText: string,
  rolePlayingAgeFormat: string
) {
  const min = minimumPlayingAge ?? anyPlayingAgeRange.min;
  const max = maximumPlayingAge ?? anyPlayingAgeRange.max;

  if (min <= anyPlayingAgeRange.min && max >= anyPlayingAgeRange.max) {
    return roleAnyPlayingAgeText;
  }

  if (min === max) {
    return rolePlayingAgeFormat.replace(/{min}\s*\S\s*{max}/g, String(min));
  }

  return rolePlayingAgeFormat
    .replace("{min}", String(min))
    .replace("{max}", String(max));
}

function prepareFacetNames(groups: IFacetGroup[], facetIds: number[]) {
  return findFacetNames(groups, facetIds).join(", ");
}

export function prepareRoleTitle(
  role: IRole,
  groups: IFacetGroup[],
  texts: IPrepareRoleTexts,
  playingAge?: string
) {
  const gender = role.anyGender
    ? texts.roleAnyGender
    : prepareFacetNames(groups, role.genderIds);

  return `${role.name} (${gender}, ${playingAge})`;
}

export interface IRoleDisabilityTranslationMapping {
  [DisabilityPreference.Undefined]: string;
  [DisabilityPreference.Any]: string;
  [DisabilityPreference.Specific]: string;
  [DisabilityPreference.None]: string;
}

export function prepareDisability(
  role: IRole,
  groups: IFacetGroup[],
  translationMapping: IRoleDisabilityTranslationMapping
) {
  let secondLine;

  if (role.disabilityPreference === DisabilityPreference.Specific) {
    secondLine =
      prepareFacetNames(groups, role.disabilityIds) +
      (role.disabilityInfo ? ` - ${role.disabilityInfo}` : "");
  } else if (role.disabilityPreference === DisabilityPreference.None) {
    secondLine = role.disabilityInfo ?? "";
  }

  return (
    translationMapping[role.disabilityPreference] +
    (secondLine ? `:<br>${secondLine}` : "")
  );
}

export function prepareGender(
  role: IRole,
  groups: IFacetGroup[],
  textRoleAnyGender: string
) {
  if (role.anyGender) {
    return textRoleAnyGender;
  }

  return (
    prepareFacetNames(groups, role.genderIds) +
    (role.genderInfo ? ` - ${role.genderInfo}` : "")
  );
}

export function prepareAppearance(
  role: IRole,
  groups: IFacetGroup[],
  textRoleAnyAppearance: string
) {
  if (role.anyAppearance) {
    return textRoleAnyAppearance;
  }

  return prepareFacetNames(groups, role.appearanceIds);
}

export function prepareAttachmentLink(attachmentId: string) {
  return `${attachmentsApiUrl}/${attachmentId}`;
}

const FILE_EXTENSIONS = [".pdf", ".jpeg", ".jpg", ".png"];

export function isPreviewFileAvailable(fileName: string) {
  return FILE_EXTENSIONS.some((extension) => fileName.endsWith(extension));
}

export function prepareAttachmentTarget(fileName: string) {
  return isPreviewFileAvailable(fileName) ? "_blank" : undefined;
}

export function prepareViewSuggestionsLink(
  breakdownData: IPrepareViewSuggestionsLinkData
): string {
  const parentId =
    breakdownData.linkmailType === LinkmailType.Amendment
      ? breakdownData.amendmentParentId
      : breakdownData.id;

  return `/viewsuggestions/${parentId}?repliesTo=${breakdownData.id}`;
}

export function preparePlayingAgeOptions(): IOption<number>[] {
  return Array(anyPlayingAgeRange.max - anyPlayingAgeRange.min + 1)
    .fill(1)
    .map((_, index) => {
      return {
        value: index + anyPlayingAgeRange.min,
        name: String(index + anyPlayingAgeRange.min),
      };
    });
}

export function prepareFtOptions(): IOption<number>[] {
  const inchesInFoot = 12;
  const minImperial = convertMetricToImperial(
    convertMetersToCentimeters(anyHeightRange.min)
  );
  const maxImperial = convertMetricToImperial(
    convertMetersToCentimeters(anyHeightRange.max)
  );
  const min = minImperial.feet * inchesInFoot + minImperial.inches;
  const max = maxImperial.feet * inchesInFoot + maxImperial.inches;

  return Array(max - min + 1)
    .fill(1)
    .map((_, index) => {
      const value = index + min;
      const base = value / inchesInFoot;
      const feet =
        base < 1
          ? Math.floor(base)
          : Math.ceil(Number.isInteger(base) ? base : base - 1);
      const inches = value % inchesInFoot;

      return {
        value: convertImperialToMetric(feet, inches),
        name: formatImperialLength(feet, inches),
      };
    });
}

export function prepareCmOptions(): IOption<number>[] {
  const min = convertMetersToCentimeters(anyHeightRange.min);
  const max = convertMetersToCentimeters(anyHeightRange.max);

  return Array(max - min + 1)
    .fill(1)
    .map((_, index) => {
      const value = index + min;

      return { value, name: `${value} cm` };
    });
}
